import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Mail, Phone, MapPin, FileText, DollarSign, Printer, Download, Filter, X, Eye, ExternalLink } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

// Helper function to get correct file URL
const getFileUrl = (filePath) => {
  if (!filePath) return '';
  // Handle both old format (/uploads/...) and new format (/api/uploads/...)
  if (filePath.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
  }
  // Convert /uploads/customers/filename to /api/uploads/customers/filename
  return `${process.env.REACT_APP_BACKEND_URL}/api${filePath}`;
};

// Helper function to process Arabic text for proper display in PDF
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const hasArabic = (text) => {
  if (!text) return false;
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    fetchCustomerHistory();
  }, [customerId]);

  const fetchCustomerHistory = async (fromDate = '', toDate = '') => {
    try {
      setLoading(true);
      let url = `/customers/${customerId}/history`;
      const params = new URLSearchParams();
      if (fromDate) params.append('date_from', fromDate);
      if (toDate) params.append('date_to', toDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axiosInstance.get(url);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    fetchCustomerHistory(dateFrom, dateTo);
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchCustomerHistory();
    setShowFilter(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customer statement...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.customer) {
    return (
      <div className="text-center py-12" data-testid="customer-not-found">
        <p className="text-gray-600">Customer not found</p>
        <button onClick={() => navigate('/customers')} className="btn-primary mt-4">
          Back to Customers
        </button>
      </div>
    );
  }

  const { customer, contracts = [], projects = [], invoices = [], quotations = [], payments = [], stats } = data;

  // Combine all transactions and sort by date
  const allTransactions = [
    ...(Array.isArray(contracts) ? contracts : []).map(c => ({
      date: c?.created_at || c?.start_date || null,
      type: 'Contract',
      reference: c?.contract_number || '',
      description: c?.contract_title || '',
      debit: Number(c?.contract_value) || 0,
      credit: 0,
      status: c?.status || ''
    })),
    ...(Array.isArray(invoices) ? invoices : []).map(inv => ({
      date: inv?.created_at || inv?.invoice_date || null,
      type: 'Invoice',
      reference: inv?.invoice_number || inv?.invoice_no || '',
      description: `Invoice`,
      debit: Number(inv?.total) || 0,
      credit: 0,
      status: inv?.status || ''
    })),
    ...(Array.isArray(payments) ? payments : []).map(p => ({
      date: p?.payment_date || null,
      type: 'Payment',
      reference: p?.payment_no || p?.payment_number || '',
      description: `Payment - ${p?.payment_method || ''}`,
      debit: 0,
      credit: Number(p?.amount) || 0,
      status: 'completed'
    }))
  ].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return (isFinite(da) ? da : 0) - (isFinite(db) ? db : 0);
  });

  // Calculate running balance (Invoices add to balance, Payments reduce it)
  let runningBalance = 0;
  const transactionsWithBalance = allTransactions.filter(t => t.type !== 'Contract').map(t => {
    runningBalance += t.debit - t.credit;
    return { ...t, balance: runningBalance };
  });

  const handlePrintPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Header - Company info
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 33, 168);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), pageWidth - 14, 10, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Distinct High Road', 14, 12);
    
    // Horizontal line
    doc.setDrawColor(107, 33, 168);
    doc.setLineWidth(0.5);
    doc.line(14, 16, pageWidth - 14, 16);
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(107, 33, 168);
    doc.text('Customer Statement', pageWidth / 2, 26, { align: 'center' });
    
    // Arabic subtitle
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('كشف حساب العميل'), pageWidth / 2, 33, { align: 'center' });
    
    // Customer Info
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(customer.name_english || '', 14, 45);
    
    if (customer.name_arabic) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.setFontSize(11);
      doc.text(processArabicText(customer.name_arabic), 14, 52);
    }
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Customer #: ${customer.customer_number}`, 14, 59);
    doc.text(`Email: ${customer.email || 'N/A'}`, 14, 65);
    if (customer.vat_number) doc.text(`VAT: ${customer.vat_number}`, 14, 71);
    
    // Date range
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 14, 45, { align: 'right' });
    if (dateFrom || dateTo) {
      doc.text(`Period: ${dateFrom || 'Start'} to ${dateTo || 'Now'}`, pageWidth - 14, 52, { align: 'right' });
    }
    
    // Summary Box
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(14, 78, pageWidth - 28, 22, 3, 3, 'F');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Total Debit', 22, 86);
    doc.text('Total Credit', 72, 86);
    doc.text('Balance', 122, 86);
    
    // Arabic labels
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(8);
    doc.text(processArabicText('مدين'), 45, 86);
    doc.text(processArabicText('دائن'), 97, 86);
    doc.text(processArabicText('الرصيد'), 145, 86);
    
    const totalDebitCalc = transactionsWithBalance.reduce((sum, t) => sum + t.debit, 0);
    const totalCreditCalc = transactionsWithBalance.reduce((sum, t) => sum + t.credit, 0);
    const finalBalanceCalc = totalDebitCalc - totalCreditCalc;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`${totalDebitCalc.toLocaleString()} SAR`, 22, 94);
    doc.text(`${totalCreditCalc.toLocaleString()} SAR`, 72, 94);
    doc.setTextColor(finalBalanceCalc >= 0 ? 220 : 34, finalBalanceCalc >= 0 ? 38 : 197, finalBalanceCalc >= 0 ? 38 : 94);
    doc.text(`${finalBalanceCalc.toLocaleString()} SAR`, 122, 94);
    
    // Transactions Table
    if (transactionsWithBalance.length > 0) {
      autoTable(doc, {
        startY: 108,
        head: [['Date', 'Type', 'Reference', 'Description', 'Debit', 'Credit', 'Balance']],
        body: transactionsWithBalance.map(t => [
          new Date(t.date).toLocaleDateString(),
          t.type,
          t.reference,
          t.description,
          t.debit > 0 ? t.debit.toLocaleString() : '-',
          t.credit > 0 ? t.credit.toLocaleString() : '-',
          t.balance.toLocaleString()
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [107, 33, 168] },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        columnStyles: {
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Page ${i} of ${pageCount} - Distinct High Road Accounting System`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Download PDF directly
    doc.save(`Customer_Statement_${customer.customer_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const totalDebit = transactionsWithBalance.reduce((sum, t) => sum + t.debit, 0);
  const totalCredit = transactionsWithBalance.reduce((sum, t) => sum + t.credit, 0);
  const finalBalance = totalDebit - totalCredit;

  return (
    <div data-testid="customer-details-page" className="print:p-4">
      {/* Header */}
      <div className="mb-6 print:mb-4">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 print:hidden"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-purple-600 mb-1">Customer Statement</h1>
            <p className="text-gray-500 text-sm mb-3">كشف حساب العميل</p>
            <p className="text-sm text-purple-600 font-semibold mb-1">{customer.customer_number}</p>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{customer.name_english}</h2>
            <p className="text-lg text-gray-600" dir="rtl">{customer.name_arabic}</p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <div className="relative">
              <button
                onClick={() => setShowFilter(!showFilter)}
                className={`btn-secondary flex items-center gap-2 ${(dateFrom || dateTo) ? 'ring-2 ring-purple-500' : ''}`}
              >
                <Filter className="w-4 h-4" />
                Filter
                {(dateFrom || dateTo) && <span className="w-2 h-2 bg-purple-500 rounded-full"></span>}
              </button>
              
              {showFilter && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 w-80">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-gray-900">Filter by Date</h4>
                    <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">From Date</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">To Date</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button onClick={handleClearFilter} className="flex-1 px-3 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
                        Clear
                      </button>
                      <button onClick={handleApplyFilter} className="flex-1 px-3 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700">
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => window.print()} className="btn-secondary flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button onClick={handlePrintPDF} className="btn-primary flex items-center gap-2">
              <Download className="w-4 h-4" />
              Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 print:border print:mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Email</p>
            <p className="font-semibold">{customer.email || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Phone</p>
            <p className="font-semibold">{customer.mobile || customer.phone || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">VAT Number</p>
            <p className="font-semibold">{customer.vat_number || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">CR Number</p>
            <p className="font-semibold">{customer.cr_number || '-'}</p>
          </div>
        </div>
      </div>

      {/* Attached Documents */}
      {(customer.national_address_file || customer.cr_file || customer.vat_file) && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 print:hidden">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Attached Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {customer.national_address_file && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">National Address</p>
                    <p className="text-xs text-gray-500">العنوان الوطني</p>
                  </div>
                </div>
                <a
                  href={getFileUrl(customer.national_address_file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4" />
                  View
                </a>
              </div>
            )}
            {customer.cr_file && (
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="font-semibold text-gray-900">CR Certificate</p>
                    <p className="text-xs text-gray-500">السجل التجاري</p>
                  </div>
                </div>
                <a
                  href={getFileUrl(customer.cr_file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Eye className="w-4 h-4" />
                  View
                </a>
              </div>
            )}
            {customer.vat_file && (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">VAT Certificate</p>
                    <p className="text-xs text-gray-500">شهادة الضريبة</p>
                  </div>
                </div>
                <a
                  href={getFileUrl(customer.vat_file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Eye className="w-4 h-4" />
                  View
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6 print:grid-cols-6 print:mb-4">
        <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
          <p className="text-purple-600 text-xs font-medium mb-1">Contracts</p>
          <p className="text-xl font-bold text-purple-700">{stats?.total_contracts || contracts.length}</p>
          <p className="text-xs text-purple-500">{(stats?.total_contract_value || 0).toLocaleString()} SAR</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
          <p className="text-blue-600 text-xs font-medium mb-1">Projects</p>
          <p className="text-xl font-bold text-blue-700">{stats?.total_projects || projects.length}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-100">
          <p className="text-yellow-600 text-xs font-medium mb-1">Quotations</p>
          <p className="text-xl font-bold text-yellow-700">{quotations.length}</p>
          <p className="text-xs text-yellow-500">{(stats?.total_quoted || 0).toLocaleString()} SAR</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 border border-red-100">
          <p className="text-red-600 text-xs font-medium mb-1">Invoiced (مدين)</p>
          <p className="text-xl font-bold text-red-700">{(stats?.total_invoiced || totalDebit).toLocaleString()}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 border border-green-100">
          <p className="text-green-600 text-xs font-medium mb-1">Paid (دائن)</p>
          <p className="text-xl font-bold text-green-700">{(stats?.total_paid || totalCredit).toLocaleString()}</p>
        </div>
        <div className={`rounded-xl p-3 border ${finalBalance >= 0 ? 'bg-orange-50 border-orange-100' : 'bg-teal-50 border-teal-100'}`}>
          <p className={`text-xs font-medium mb-1 ${finalBalance >= 0 ? 'text-orange-600' : 'text-teal-600'}`}>
            Balance
          </p>
          <p className={`text-xl font-bold ${finalBalance >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>
            {(stats?.balance_due || Math.abs(finalBalance)).toLocaleString()}
          </p>
          <p className={`text-xs ${finalBalance >= 0 ? 'text-orange-500' : 'text-teal-500'}`}>SAR</p>
        </div>
      </div>

      {/* Date Range Info */}
      {(dateFrom || dateTo) && (
        <div className="bg-purple-50 rounded-lg p-3 mb-4 text-sm text-purple-700">
          <strong>Period:</strong> {dateFrom || 'Start'} to {dateTo || 'Current'}
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden print:border">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900">Account Statement</h3>
          <p className="text-sm text-gray-500">كشف الحساب</p>
        </div>
        
        {transactionsWithBalance.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-purple-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Reference</th>
                  <th className="px-4 py-3 text-left">Description</th>
                  <th className="px-4 py-3 text-right">Debit (مدين)</th>
                  <th className="px-4 py-3 text-right">Credit (دائن)</th>
                  <th className="px-4 py-3 text-right">Balance (الرصيد)</th>
                </tr>
              </thead>
              <tbody>
                {transactionsWithBalance.map((t, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3">{t.date ? new Date(t.date).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        t.type === 'Payment' ? 'bg-green-100 text-green-700' :
                        t.type === 'Invoice' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {t.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">{t.reference}</td>
                    <td className="px-4 py-3 text-gray-600">{t.description}</td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">
                      {t.debit > 0 ? t.debit.toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">
                      {t.credit > 0 ? t.credit.toLocaleString() : '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${t.balance >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                      {t.balance.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-bold">
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-right">Total:</td>
                  <td className="px-4 py-3 text-right text-red-600">{totalDebit.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-green-600">{totalCredit.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right ${finalBalance >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                    {finalBalance.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
